// src/features/units/OrderViewer.js
import React, { useState, useEffect, useRef } from 'react';
import './OrderViewer.css';

const OrderViewer = ({
    unit,
    userAnswers,
    showFeedback,
    onAnswerSelect,
    onCheckAnswer,
    onMarkCompleted,
    onResetUnit  
}) => {
    // Parse unit content
    const [content, setContent] = useState(null);
    const [items, setItems] = useState([]);
    const [hasAnswered, setHasAnswered] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dropZoneIndex, setDropZoneIndex] = useState(null);
    
    // Touch handling for mobile
    // Refs for touch handling
    const containerRef = useRef(null);

    // Parse content on mount or when unit changes
    useEffect(() => {
        if (!unit?.content) return;

        // Reset interactive state on every unit change
            setHasAnswered(false);
            setIsCorrect(false);
            setDraggedIndex(null);
            setDropZoneIndex(null);

        try {
            const parsed = typeof unit.content === 'string' 
                ? JSON.parse(unit.content) 
                : unit.content;
            
            setContent(parsed);
            
            // Initialize items with shuffled order for the learner
            if (parsed.items && parsed.items.length > 0) {
                // Create a shuffled copy for the learner to reorder
                const shuffledItems = [...parsed.items]
                    .map(item => ({ ...item, currentPosition: 0 }))
                    .sort(() => Math.random() - 0.5);
                
                // Assign current positions based on shuffled order
                const itemsWithPositions = shuffledItems.map((item, index) => ({
                    ...item,
                    currentPosition: index + 1
                }));
                
                setItems(itemsWithPositions);
            }
        } catch (error) {
            console.error('Error parsing order question content:', error);
            setContent({ instructions: 'Error loading question', items: [] });
        }
    }, [unit?.content]);

    // Load previous answer if exists
    useEffect(() => {
        if (userAnswers && userAnswers[unit?.id]) {
            const previousAnswer = userAnswers[unit.id];
            if (previousAnswer.order) {
                setItems(previousAnswer.order);
                setHasAnswered(true);
                
                if (showFeedback) {
                    checkAnswer(previousAnswer.order);
                }
            }
        }
    }, [userAnswers, unit?.id, showFeedback]);

    // Only load saved answer on unit ID change, guard against empty answers
        useEffect(() => {
            if (!unit?.id || !userAnswers) return;

            const previousAnswer = userAnswers[unit.id];
            if (!previousAnswer?.order || previousAnswer.order.length === 0) return; // ✅ no saved answer = stay clean

            setItems(previousAnswer.order);
            setHasAnswered(true);

            if (showFeedback) {
                checkAnswer(previousAnswer.order);
            }
        }, [unit?.id]); 

    // Reset handler - clears both database and local state
    const handleReset = () => {
        console.log('🔄 Reset button clicked for order question');
        
        if (onResetUnit && unit?.id) {
            // Reset database/global state
            onResetUnit(unit.id);
            
            // ALSO reset local component state immediately for instant visual feedback
            setHasAnswered(false);
            setIsCorrect(false);
            setDraggedIndex(null);
            setDropZoneIndex(null);
            
            // Re-shuffle items for better learning
            if (content?.items && content.items.length > 0) {
                const reshuffledItems = [...content.items]
                    .map(item => ({ ...item, currentPosition: 0 }))
                    .sort(() => Math.random() - 0.5);
                
                const itemsWithNewPositions = reshuffledItems.map((item, index) => ({
                    ...item,
                    currentPosition: index + 1
                }));
                
                setItems(itemsWithNewPositions);
            }
            
            console.log('✅ Order question reset complete - both database and local state');
        } else {
            console.error('❌ Reset failed - missing onResetUnit or unit.id');
        }
    };

    // Check if the current order is correct
    const checkAnswer = (itemsToCheck = items) => {
        if (!content?.items || itemsToCheck.length === 0) return false;
        
        // Check if each item is in its correct position
        const correct = itemsToCheck.every((item, index) => 
            item.correctPosition === index + 1
        );
        
        setIsCorrect(correct);
        return correct;
    };

    // Handle drag start
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        
        // Add visual feedback
        setTimeout(() => {
            e.target.classList.add('dragging');
        }, 0);
    };

    // Handle drag end
    const handleDragEnd = (e) => {
        e.target.classList.remove('dragging');
        setDraggedIndex(null);
        setDropZoneIndex(null);
    };

    // Handle drag over
    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (index !== draggedIndex) {
            setDropZoneIndex(index);
        }
    };

    // Handle drag leave
    const handleDragLeave = () => {
        setDropZoneIndex(null);
    };

    // Handle drop
    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        setDropZoneIndex(null);
        
        if (draggedIndex === null || draggedIndex === dropIndex) return;
        
        moveItem(draggedIndex, dropIndex);
    };

    // Move item from one position to another
    const moveItem = (fromIndex, toIndex) => {
        const newItems = [...items];
        const draggedItem = newItems[fromIndex];
        
        // Remove item from old position
        newItems.splice(fromIndex, 1);
        
        // Insert item at new position
        newItems.splice(toIndex, 0, draggedItem);
        
        // Update current positions
        const updatedItems = newItems.map((item, index) => ({
            ...item,
            currentPosition: index + 1
        }));
        
        setItems(updatedItems);
        
        // Don't auto-save - let user submit when ready
        // User can rearrange multiple times before submitting
    };

    // Touch handling for mobile devices
    const handleTouchStart = (e, index) => {
        setDraggedIndex(index);
        
        // Add visual feedback
        e.currentTarget.classList.add('dragging');
        
        // Prevent default to avoid scrolling
        e.preventDefault();
    };

    const handleTouchMove = (e) => {
        if (draggedIndex === null) return;
        
        e.preventDefault();
        const touch = e.touches[0];
        const currentY = touch.clientY;
        
        // Find which item we're over
        const container = containerRef.current;
        if (!container) return;
        
        const itemElements = container.querySelectorAll('.order-item');
        let newDropIndex = null;
        
        for (let i = 0; i < itemElements.length; i++) {
            const rect = itemElements[i].getBoundingClientRect();
            if (currentY >= rect.top && currentY <= rect.bottom) {
                newDropIndex = i;
                break;
            }
        }
        
        setDropZoneIndex(newDropIndex);
    };

    const handleTouchEnd = () => {
        if (draggedIndex === null) return;
        
        // Remove visual feedback
        document.querySelectorAll('.order-item').forEach(el => {
            el.classList.remove('dragging');
        });
        
        // Perform the move if we have a valid drop zone
        if (dropZoneIndex !== null && dropZoneIndex !== draggedIndex) {
            moveItem(draggedIndex, dropZoneIndex);
        }
        
        setDraggedIndex(null);
        setDropZoneIndex(null);
    };

    // Handle check answer button
    const handleCheckAnswer = () => {
        const correct = checkAnswer();
        setHasAnswered(true);
        
        // Save the final answer
        if (onAnswerSelect) {
            onAnswerSelect(unit.id, { order: items });
        }
        
        if (onCheckAnswer) {
            onCheckAnswer(unit.id, { order: items }, correct);
        }
        
        // Mark as correct or wrong
        if (onMarkCompleted) {
            onMarkCompleted(unit.id, null, correct ? 'correct' : 'wrong');
        }
    };

    // Keyboard navigation support
    const handleKeyDown = (e, index) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            // Toggle selection or move logic here if needed
        } else if (e.key === 'ArrowUp' && index > 0) {
            e.preventDefault();
            moveItem(index, index - 1);
        } else if (e.key === 'ArrowDown' && index < items.length - 1) {
            e.preventDefault();
            moveItem(index, index + 1);
        }
    };

    if (!content) {
        return (
            <div className="order-content">
                <p>Loading order question...</p>
            </div>
        );
    }

    return (
        <div className="order-content">
            {/* Instructions */}
            {content.instructions && (
                <div 
                    className="order-stem"
                    dangerouslySetInnerHTML={{ __html: content.instructions }}
                />
            )}

            {/* Drag and drop instructions */}
            <div className="order-instructions">
                <span className="desktop-instruction">
                    💡 Drag and drop items to arrange them in the correct order
                </span>
                <span className="mobile-instruction">
                    💡 Touch and drag items to rearrange them
                </span>
            </div>

            {/* Order items */}
            <div 
                className="order-items"
                ref={containerRef}
            >
                {items.map((item, index) => (
                    <React.Fragment key={item.id}>
                        {/* Drop zone indicator */}
                        {dropZoneIndex === index && draggedIndex !== index && (
                            <div className="drop-zone active"></div>
                        )}
                        
                        <div
                            className={`order-item ${
                                hasAnswered && showFeedback 
                                    ? (item.correctPosition === index + 1 ? 'correct' : 'incorrect')
                                    : ''
                            } ${hasAnswered ? 'locked' : ''}`}
                            draggable={!hasAnswered}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onTouchStart={(e) => handleTouchStart(e, index)}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            tabIndex={hasAnswered ? -1 : 0}
                            role="button"
                            aria-label={`Item ${index + 1}: ${item.text}. ${hasAnswered ? 'Answer submitted' : 'Press arrow keys to move, space to select'}`}
                        >
                            {/* Drag handle */}
                            {!hasAnswered && (
                                <div className="drag-handle">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            )}
                            
                            {/* Position indicator */}
                            <div className="position-indicator">
                                {index + 1}
                            </div>
                            
                            {/* Item content */}
                            <div className="order-item-content">
                                {item.text}
                            </div>
                            
                            {/* Feedback indicator */}
                            {hasAnswered && showFeedback && (
                                <div className="feedback-indicator">
                                    {item.correctPosition === index + 1 ? '✓' : '✗'}
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                ))}
                
                {/* Final drop zone */}
                {dropZoneIndex === items.length && (
                    <div className="drop-zone active"></div>
                )}
            </div>

            {/* Submit Answer button */}
            {!hasAnswered && items.length > 0 && (
                <button 
                    className="check-answer-button"
                    onClick={handleCheckAnswer}
                >
                    Submit Answer
                </button>
            )}

            {/* Feedback */}
            {hasAnswered && showFeedback && (
                <div className={`feedback-container ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="feedback-header">
                        {isCorrect ? '✅' : '❌'}
                    </div>
                    <div className="feedback-text">
                        {isCorrect 
                            ? 'Great job! You arranged the items in the correct order.'
                            : 'Not quite right. Look at the highlighted items to see the correct positions.'
                        }
                    </div>
                    {content.generalFeedback && (
                        <div className="general-feedback">
                            {content.generalFeedback}
                        </div>
                    )}
                    {/* Reset button below feedback text */}
                    <div className="reset-container" style={{ textAlign: 'center', marginTop: '15px' }}>
                        <button 
                            className="reset-button"
                            onClick={handleReset}
                            title="Try again"
                            style={{
                                padding: '8px 12px',
                                fontSize: '18px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#5a6268';
                                e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = '#6c757d';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            🔄
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderViewer;